/**
 * Investee Dashboard — "What's Due" View
 *
 * The primary landing page for investee_reporter users.
 * Shows:
 *   - Pending submissions with fund, period, status, completion, deadline
 *   - Submission history table with status badges
 *   - Benchmarks placeholder (coming soon)
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardCheck,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  FileText,
  BarChart2,
} from 'lucide-react';
import { clsx } from 'clsx';
import { format, parseISO, differenceInDays } from 'date-fns';
import { apiClient } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { StatusBadge, CompletionBar } from '@/components/ui/StatusBadge';
import { useTranslation } from '@/i18n';
import { formatStatus } from '@/utils/formatStatus';

// ─── Types ──────────────────────────────────────────────────────

interface PendingSubmission {
  id: number;
  fund_id: number;
  fund_name: string;
  template_id: number;
  template_name?: string;
  investee_id: number;
  reporting_period: string;
  status: string;
  completion_percent: number;
  deadline?: string | null;
  created_at: string;
}

interface SubmissionHistoryItem {
  id: number;
  fund_name: string;
  reporting_period: string;
  status: string;
  completion_percent: number;
  submitted_at?: string | null;
  approved_at?: string | null;
  created_at: string;
}

interface InvesteeDashboardData {
  pending_submissions: PendingSubmission[];
  submission_history: SubmissionHistoryItem[];
  investee_name?: string;
}

// ─── Status Variants ────────────────────────────────────────────

const STATUS_VARIANTS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'danger'> = {
  draft: 'default',
  submitted: 'info',
  validated: 'warning',
  approved: 'success',
  rejected: 'danger',
};

// ─── Main Component ─────────────────────────────────────────────

export function InvesteeDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const investeeId = user?.id;

  const dashboardQuery = useQuery<InvesteeDashboardData>({
    queryKey: ['ic', 'investee-dashboard', investeeId],
    queryFn: async () => {
      const res = await apiClient.get(
        `/api/v1/impact-core/dashboard/investee-dashboard?investee_id=${investeeId}`,
      );
      return res.data?.data ?? res.data;
    },
    staleTime: 30_000,
    enabled: !!investeeId,
  });

  const pending = dashboardQuery.data?.pending_submissions ?? [];
  const history = dashboardQuery.data?.submission_history ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-ic-text-primary">
          {dashboardQuery.data?.investee_name
            ? `Welcome, ${dashboardQuery.data.investee_name}`
            : 'Investee Dashboard'}
        </h1>
        <p className="text-xs text-ic-text-tertiary">Your submissions overview and pending actions</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Pending"
          value={pending.filter((s) => s.status === 'draft').length.toString()}
          icon={<ClipboardCheck className="h-4 w-4" />}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
          loading={dashboardQuery.isLoading}
        />
        <StatCard
          label="Submitted"
          value={pending.filter((s) => s.status === 'submitted').length.toString()}
          icon={<Clock className="h-4 w-4" />}
          color="text-amber-400"
          bgColor="bg-amber-500/10"
          loading={dashboardQuery.isLoading}
        />
        <StatCard
          label="Approved"
          value={history.filter((s) => s.status === 'approved').length.toString()}
          icon={<CheckCircle className="h-4 w-4" />}
          color="text-emerald-400"
          bgColor="bg-emerald-500/10"
          loading={dashboardQuery.isLoading}
        />
        <StatCard
          label="Rejected"
          value={history.filter((s) => s.status === 'rejected').length.toString()}
          icon={<AlertCircle className="h-4 w-4" />}
          color="text-red-400"
          bgColor="bg-red-500/10"
          loading={dashboardQuery.isLoading}
        />
      </div>

      {/* Pending Submissions */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
            Pending Submissions
          </h2>
          <button
            onClick={() => navigate('/submit')}
            className="flex items-center gap-1 text-[10px] text-ic-accent hover:underline"
          >
            New Submission
            <ArrowRight className="h-3 w-3" />
          </button>
        </div>

        {dashboardQuery.isLoading ? (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 animate-pulse rounded-lg bg-slate-700/30" />
            ))}
          </div>
        ) : pending.length === 0 ? (
          <div className="rounded-lg border border-ic-border bg-ic-card p-8">
            <div className="flex flex-col items-center gap-2">
              <CheckCircle className="h-6 w-6 text-emerald-400" />
              <p className="text-xs text-ic-text-tertiary">No pending submissions. You are up to date.</p>
            </div>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {pending.map((submission) => (
              <PendingCard
                key={submission.id}
                submission={submission}
                onAction={() => navigate('/submit')}
              />
            ))}
          </div>
        )}
      </div>

      {/* Submission History */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
            Submission History
          </h2>
          <button
            onClick={() => navigate('/submissions')}
            className="flex items-center gap-1 text-[10px] text-ic-accent hover:underline"
          >
            View All
            <ArrowRight className="h-3 w-3" />
          </button>
        </div>

        {dashboardQuery.isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-slate-700/30" />
            ))}
          </div>
        ) : history.length === 0 ? (
          <div className="rounded-lg border border-ic-border bg-ic-card p-8">
            <div className="flex flex-col items-center gap-2">
              <FileText className="h-5 w-5 text-ic-text-tertiary" />
              <p className="text-xs text-ic-text-tertiary">No submission history yet.</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
            <table className="w-full text-xs ic-table">
              <thead>
                <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                  <th className="px-4 pb-2 pt-3 font-medium">Fund</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Period</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Status</th>
                  <th className="px-4 pb-2 pt-3 font-medium w-32">Completion</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 10).map((item) => (
                  <tr key={item.id} className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50">
                    <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">{item.fund_name}</td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{item.reporting_period}</td>
                    <td className="py-2.5 px-4">
                      <StatusBadge label={formatStatus(item.status, t)} variant={STATUS_VARIANTS[item.status] ?? 'default'} />
                    </td>
                    <td className="py-2.5 px-4">
                      <CompletionBar percent={item.completion_percent} />
                    </td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-tertiary">
                      {item.submitted_at
                        ? format(parseISO(item.submitted_at), 'dd MMM yyyy')
                        : item.approved_at
                          ? format(parseISO(item.approved_at), 'dd MMM yyyy')
                          : '--'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Benchmarks Placeholder */}
      <div>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
          Benchmarks
        </h2>
        <div className="rounded-lg border border-dashed border-ic-border bg-ic-card/50 p-8">
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ic-accent/10">
              <BarChart2 className="h-5 w-5 text-ic-accent" />
            </div>
            <div className="text-center">
              <h3 className="text-sm font-medium text-ic-text-primary">Benchmarks Coming Soon</h3>
              <p className="mt-1 max-w-sm text-xs text-ic-text-tertiary">
                Compare your ESG performance against sector peers and fund-level targets.
                Benchmark data will appear here once sufficient submissions have been collected.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-Components ─────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  loading?: boolean;
}

function StatCard({ label, value, icon, color, bgColor, loading }: StatCardProps) {
  if (loading) {
    return (
      <div className="rounded-lg border border-ic-border bg-ic-card p-4">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 animate-pulse rounded-full bg-slate-700/50" />
          <div className="space-y-1.5">
            <div className="h-5 w-8 animate-pulse rounded bg-slate-700/50" />
            <div className="h-3 w-14 animate-pulse rounded bg-slate-700/30" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-ic-border bg-ic-card p-4">
      <div className="flex items-center gap-3">
        <div className={clsx('flex h-8 w-8 items-center justify-center rounded-full', bgColor)}>
          <span className={color}>{icon}</span>
        </div>
        <div>
          <p className="text-xl font-semibold text-ic-text-primary tracking-tight">{value}</p>
          <p className="text-[10px] uppercase tracking-wider text-ic-text-tertiary">{label}</p>
        </div>
      </div>
    </div>
  );
}

interface PendingCardProps {
  submission: PendingSubmission;
  onAction: () => void;
}

function PendingCard({ submission, onAction }: PendingCardProps) {
  const { t } = useTranslation();
  const daysRemaining = (() => {
    if (!submission.deadline) return null;
    try { return differenceInDays(parseISO(submission.deadline), new Date()); } catch { return null; }
  })();

  const isOverdue = daysRemaining !== null && daysRemaining < 0;
  const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 7;

  return (
    <div className="rounded-lg border border-ic-border bg-ic-card p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-xs font-semibold text-ic-text-primary">{submission.fund_name}</h3>
          <p className="text-[10px] text-ic-text-tertiary">{submission.reporting_period}</p>
          {submission.template_name && (
            <p className="text-[10px] text-ic-text-tertiary mt-0.5">{submission.template_name}</p>
          )}
        </div>
        <StatusBadge
          label={formatStatus(submission.status, t)}
          variant={STATUS_VARIANTS[submission.status] ?? 'default'}
        />
      </div>

      {/* Completion */}
      <CompletionBar
        percent={submission.completion_percent}
        label="Completion"
        className="mb-3"
      />

      {/* Deadline */}
      {daysRemaining !== null && (
        <div className={clsx(
          'mb-3 flex items-center gap-1.5 text-[10px] font-medium',
          isOverdue ? 'text-red-400' : isUrgent ? 'text-amber-400' : 'text-ic-text-tertiary',
        )}>
          <Clock className="h-3 w-3" />
          {isOverdue
            ? `Overdue by ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) !== 1 ? 's' : ''}`
            : `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`}
        </div>
      )}

      {/* Action Button */}
      <button
        onClick={onAction}
        className="flex w-full items-center justify-center gap-1 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
      >
        {submission.status === 'draft' && submission.completion_percent > 0 ? (
          <>Continue<ArrowRight className="h-3.5 w-3.5" /></>
        ) : submission.status === 'rejected' ? (
          <>Resubmit<ArrowRight className="h-3.5 w-3.5" /></>
        ) : (
          <>Start Submission<ArrowRight className="h-3.5 w-3.5" /></>
        )}
      </button>
    </div>
  );
}
